import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { refreshStages } from "@/lib/stages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ArrowUp, ArrowDown, Eye, EyeOff, Trash2, Lock, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function StagesEditor() {
  const [stages, setStages] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [delStage, setDelStage] = useState(null);

  const load = () => api.get("/settings/stages").then(({ data }) => { setStages(data.stages); setDirty(false); });
  useEffect(() => { load(); }, []);

  const edit = (next) => { setStages(next); setDirty(true); };
  const move = (i, d) => { const n = [...stages]; [n[i], n[i + d]] = [n[i + d], n[i]]; edit(n); };
  const update = (i, patch) => edit(stages.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const add = () => { if (!newLabel.trim()) return; edit([...stages, { key: "", label: newLabel.trim(), hidden: false, open_deals: 0 }]); setNewLabel(""); };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/stages", { stages: stages.map(({ key, label, hidden }) => ({ key, label, hidden })) });
      setStages(data.stages); setDirty(false); refreshStages(); toast.success("Pipeline stages saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="stages-editor">
      <div className="flex items-center justify-between mb-1">
        <span className="font-heading font-bold">Pipeline stages</span>
        <Button size="sm" onClick={save} disabled={!dirty || busy} data-testid="stages-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save changes</Button>
      </div>
      <div className="text-xs text-gray-500 mb-4">Rename, reorder, hide or delete stages. Stages with open deals can't be hidden; deleting moves their deals to another stage. Handover is the locked end stage — lost deals use Reject.</div>
      <div className="space-y-1.5">
        {stages.map((s, i) => (
          <div key={s.key || `new-${i}`} className={`flex items-center gap-2 border rounded p-1.5 ${s.hidden ? "bg-gray-50 border-dashed border-gray-300" : "border-gray-200"}`} data-testid={`stage-row-${s.key || `new-${i}`}`}>
            <div className="flex flex-col">
              <button disabled={i === 0} onClick={() => move(i, -1)} className="text-gray-400 hover:text-gray-900 disabled:opacity-20" data-testid={`stage-up-${i}`}><ArrowUp className="h-3.5 w-3.5" /></button>
              <button disabled={i === stages.length - 1} onClick={() => move(i, 1)} className="text-gray-400 hover:text-gray-900 disabled:opacity-20" data-testid={`stage-down-${i}`}><ArrowDown className="h-3.5 w-3.5" /></button>
            </div>
            <Input value={s.label} onChange={(e) => update(i, { label: e.target.value })} className={`h-8 ${s.hidden ? "text-gray-400" : ""}`} data-testid={`stage-label-${i}`} />
            <span className="text-[11px] text-gray-500 w-16 shrink-0 text-right">{s.open_deals || 0} open</span>
            {s.locked ? (
              <span className="w-[68px] flex justify-center text-gray-400" title="Locked end stage"><Lock className="h-4 w-4" /></span>
            ) : (
              <>
                <Button size="icon" variant="ghost" className="h-8 w-8" title={s.hidden ? "Show" : "Hide"} onClick={() => update(i, { hidden: !s.hidden })} disabled={!s.hidden && s.open_deals > 0} data-testid={`stage-hide-${i}`}>
                  {s.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button size="icon" variant="ghost" className="h-8 w-8" title="Delete" onClick={() => (s.key && !dirty ? setDelStage(s) : s.key ? toast.info("Save your changes before deleting a stage") : edit(stages.filter((_, j) => j !== i)))} data-testid={`stage-delete-${i}`}>
                  <Trash2 className="h-4 w-4 text-rose-500" />
                </Button>
              </>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <Input placeholder="New stage name, e.g. Due Diligence" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} className="h-9" data-testid="stage-new-input" />
        <Button size="sm" variant="outline" onClick={add} disabled={!newLabel.trim()} data-testid="stage-add-btn"><Plus className="h-4 w-4" /> Add</Button>
      </div>
      <DeleteStageDialog stage={delStage} stages={stages} onClose={() => setDelStage(null)} onDone={() => { setDelStage(null); load(); refreshStages(); }} />
    </div>
  );
}

function DeleteStageDialog({ stage, stages, onClose, onDone }) {
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTarget(""); }, [stage]);
  const options = stages.filter((s) => s.key && s.key !== stage?.key && !s.hidden);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/settings/stages/${stage.key}/delete`, { move_to: target });
      toast.success(`Stage deleted${data.moved ? ` · ${data.moved} deal(s) moved` : ""}`); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!stage} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="delete-stage-dialog">
        <DialogHeader>
          <DialogTitle>Delete “{stage?.label}”?</DialogTitle>
          <DialogDescription>All deals in this stage ({stage?.open_deals || 0} open) will be moved to the stage you pick. Each move is logged on the deal timeline.</DialogDescription>
        </DialogHeader>
        <Select value={target} onValueChange={setTarget}>
          <SelectTrigger data-testid="delete-stage-target"><SelectValue placeholder="Move deals to…" /></SelectTrigger>
          <SelectContent className="bg-white">{options.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={confirm} disabled={!target || busy} data-testid="delete-stage-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Delete stage</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
